/**
 * The card's processing-notes lines (prod fixes 2026-10-02, PLAN.md C1, UX-SPEC section 1 "Note kinds").
 *
 * The edge sends two parallel things: `processingNotes` (the human strings every card has always shown,
 * `ⓘ {text}`) and, from 2026-10-02, `noteCodes` -- a stable `code` and a `kind` for the notes the new
 * fixes write, each carrying the same text. The card shows their UNION:
 *
 *  - a structured entry whose text equals a legacy string takes that string's place (same position), with
 *    its kind's cue: `info` = ⓘ + a visually hidden "Note:", `warning` = ⚠ + a hidden "Warning:" (never
 *    colour alone, the convert-options `severity: 'warning'` rule);
 *  - a legacy string with no structured twin renders exactly as today;
 *  - a structured entry with no legacy twin (a newer edge's note the list somehow lacks) is appended.
 *
 * Never matches on the human text to decide a KIND: the kind comes from the code's entry only. Pure.
 */
import { NoteCode } from '../../../types/types';
import { fmtCopy, PROD_FIXES_COPY } from '../../../constants/copy-deck.prod-fixes';

export type NoteLineKind = 'legacy' | 'info' | 'warning';

export interface NoteLine {
    text: string;
    kind: NoteLineKind;
    /** The structured note's code, for `data-note-code` and metrics; absent on a legacy line. */
    code?: string;
}

/** The visually hidden prefix a screen reader hears before the text (empty for a legacy line). */
export const NOTE_KIND_PREFIX: Record<NoteLineKind, string> = { legacy: '', info: 'Note: ', warning: 'Warning: ' };

function kindOf(nc: NoteCode): 'info' | 'warning' {
    return nc?.kind === 'warning' ? 'warning' : 'info';
}

/**
 * The text of a structured note: the edge's own text when it sent one (always, from `ProcessingNotes.add`),
 * else the deck string for its code, else null (nothing to show; the legacy line, if any, still renders).
 * `COMPRESS_GREW` and `PDF_ORIGINAL_KEPT` reuse long-standing server sentences that are not in the deck.
 */
export function noteTextOf(nc: NoteCode | null | undefined): string | null {
    if (!nc || typeof nc.code !== 'string' || !nc.code) return null;
    if (typeof nc.text === 'string' && nc.text.trim()) return nc.text;
    const p = nc.params || {};
    switch (nc.code) {
        case 'WAV_AUTO_MP3': return PROD_FIXES_COPY['X3-A2'];
        case 'ARCHIVE_NAMES_RENAMED': return PROD_FIXES_COPY['XS2-1'];
        case 'AUTO_RETRY_TRIED_TWICE': return PROD_FIXES_COPY['XR-2'];
        case 'FRAME_CLAMPED_TO_END': return p.t ? fmtCopy(PROD_FIXES_COPY['XS1-1'], { t: p.t }) : null;
        case 'COMPRESS_NO_REDUCTION': {
            const n = Number.parseInt(String(p.n ?? ''), 10);
            if (!Number.isFinite(n)) return null;
            return n < 1 ? PROD_FIXES_COPY['X3-W1b'] : fmtCopy(PROD_FIXES_COPY['X3-W1'], { n });
        }
        case 'COMPRESS_ORIGINAL_KEPT':
            switch (p.media) {
                case 'audio': return PROD_FIXES_COPY['X3-A1'];
                case 'video': return PROD_FIXES_COPY['X3-V1'];
                case 'image': return PROD_FIXES_COPY['X3-I1'];
                default: return PROD_FIXES_COPY['X3-G1'];
            }
        default: return null;
    }
}

/**
 * The lines to show, in order. [legacy] is the already-filtered `processingNotes` (the card drops the
 * frame-picker machine line first); [codes] is `result.noteCodes`, possibly absent or malformed; [context] is what
 * the card knows about its own page (`noteContextOf`), which can change a note's advice (UXM-05).
 */
export function processingNoteLines(
    legacy: readonly string[] | null | undefined,
    codes: readonly NoteCode[] | null | undefined,
    context: NoteContext = {},
): NoteLine[] {
    const structured = (Array.isArray(codes) ? codes : [])
        .map(nc => ({ nc, text: noteTextOf(nc) }))
        .filter((e): e is { nc: NoteCode; text: string } => e.text !== null)
        // `twinText` is the edge's own text, which pairs with its legacy twin; `line.text` is what the card shows.
        .map(e => ({
            twinText: e.text,
            line: { text: shownTextOf(e.nc, e.text, context), kind: kindOf(e.nc), code: e.nc.code } as NoteLine,
            used: false,
        }));
    const lines: NoteLine[] = [];
    for (const text of Array.isArray(legacy) ? legacy : []) {
        const twin = structured.find(s => !s.used && s.twinText === text);
        if (twin) {
            twin.used = true;
            lines.push(twin.line);
        } else {
            lines.push({ text, kind: 'legacy' });
        }
    }
    for (const s of structured) if (!s.used) lines.push(s.line);
    return lines;
}

/**
 * What the card knows about itself that changes a note's advice (UXM-05). Built by the card from its own page and
 * options (`noteContextOf`), never from the note's text.
 */
export interface NoteContext {
    /**
     * A compress-wav card whose codec is not MP3 inside WAV. compress-wav has no quality or target-size control, so
     * the grown note's "try a lower quality or a target size" points at controls the page does not have; its lever is
     * MP3 inside WAV (the same advice ironman's X3-W1w gives for no reduction).
     */
    compressWavNotMp3?: boolean;
}

/** The X3-W2s advice sentence ironman's `UserCopy.X3_W2_SETTINGS` ends with (the deck's X3-W1 advice). */
export const GREW_GENERIC_ADVICE = 'For a smaller file, try a lower quality or a target size.';

/**
 * UXM-05: the grown note's advice on compress-wav. Not in the section 13 deck (`copy-deck.prod-fixes.ts` is pinned to it
 * verbatim), like ironman's X3-W1w, which says the same thing for no reduction. No "Make it smaller" here: a grown card
 * does not offer that key (`makeSmallerPlacementOf`).
 */
export const GREW_WAV_ADVICE = 'For a smaller file, choose MP3 inside WAV.';

/** The card's note context: compress-wav, on a codec other than MP3 inside WAV (Auto, any PCM, ADPCM, ...). */
export function noteContextOf(slug: string | null | undefined, options: { audioCodec?: unknown } | null | undefined): NoteContext {
    if (slug !== 'compress-wav') return {};
    return { compressWavNotMp3: String(options?.audioCodec ?? '').toUpperCase() !== 'MP3' };
}

/**
 * The text a structured note shows on this card. Only `COMPRESS_GREW` on a compress-wav card changes, and only its
 * closing advice: the edge's sentence is kept up to that advice (the sizes and the reason are the edge's), and the
 * advice is swapped for the page's own. The code decides; the text is only checked to end with the one known advice
 * sentence, so an edge that already words it differently is shown as it is.
 */
function shownTextOf(nc: NoteCode, text: string, context: NoteContext): string {
    if (nc.code !== 'COMPRESS_GREW' || context.compressWavNotMp3 !== true) return text;
    if (!text.endsWith(GREW_GENERIC_ADVICE)) return text;
    return `${text.slice(0, text.length - GREW_GENERIC_ADVICE.length)}${GREW_WAV_ADVICE}`;
}

/** A line's identity for the "announce only notes that arrive while on screen" rule. */
export function noteLineKey(line: NoteLine): string {
    return `${line.kind}|${line.code ?? ''}|${line.text}`;
}
